import React, { useState, useMemo } from 'react';
import { Card, CardContent, Typography, Button, Grid, Chip, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, TooltipProps } from 'recharts';
import { Expense } from '../types/Expense';
import { FormattedMessage } from 'react-intl';
import { designTokens } from '../theme/designTokens';

interface CategoryExpenseChartProps {
  expenses: Expense[];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'];

const CategoryExpenseChart: React.FC<CategoryExpenseChartProps> = ({ expenses }) => {
  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      return (expense.payer === "Bruno" || expense.shared || (expense.isDebt && expense.debtFrom === "Bruno"));
    }).map(expense => {
      let amount = expense.amount;
      if (expense.shared) {
        amount = amount / 2;
      }
      if (expense.isDebt && expense.debtFrom !== "Bruno") {
        amount = 0;
      }
      return { ...expense, amount };
    });
  }, [expenses]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(filteredExpenses.map(e => e.category)));
    return uniqueCategories.sort((a, b) => a.localeCompare(b));
  }, [filteredExpenses]);

  const { startYear, endYear } = useMemo(() => {
    if (filteredExpenses.length === 0) {
      const currentYear = new Date().getFullYear();
      return { startYear: currentYear, endYear: currentYear };
    }
    const years = filteredExpenses.map(e => {
      const d = new Date(e.date);
      return isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
    });
    return {
      startYear: Math.min(...years),
      endYear: Math.max(...years)
    };
  }, [filteredExpenses]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { chartData, yearsAvailable, annualTotals } = useMemo(() => {
    const years = new Set<number>();
    filteredExpenses.forEach(e => {
      const d = new Date(e.date);
      if (!isNaN(d.getTime())) years.add(d.getFullYear());
    });
    const sortedYears = Array.from(years).sort((a, b) => a - b).map(String);

    const data: any[] = [];
    const annualTotals: Record<string, number> = {};

    sortedYears.forEach(yearStr => {
      const year = parseInt(yearStr);
      annualTotals[yearStr] = filteredExpenses
        .filter(e => {
          const d = new Date(e.date);
          return d.getFullYear() === year && selectedCategories.includes(e.category);
        })
        .reduce((sum, e) => sum + e.amount, 0);
    });

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    MONTHS.forEach((month, monthIndex) => {
      const monthData: any = { name: month };
      
      sortedYears.forEach(yearStr => {
        const year = parseInt(yearStr);
        // Skip future months for the current year
        if (year > currentYear || (year === currentYear && monthIndex > currentMonth)) {
          return;
        }

        const sumForYearMonth = filteredExpenses
          .filter(e => {
            const d = new Date(e.date);
            return d.getFullYear() === year && 
                   d.getMonth() === monthIndex && 
                   selectedCategories.includes(e.category);
          })
          .reduce((sum, e) => sum + e.amount, 0);
          
        monthData[yearStr] = sumForYearMonth;
      });
      data.push(monthData);
    });

    return { chartData: data, yearsAvailable: sortedYears, annualTotals };
  }, [filteredExpenses, selectedCategories]);

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSelectAll = () => {
    setSelectedCategories(categories);
  };

  const handleDeselectAll = () => {
    setSelectedCategories([]);
  };

  const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: 'rgba(30, 41, 59, 0.9)',
            backdropFilter: 'blur(12px)',
            border: `1px solid rgba(255, 255, 255, 0.15)`,
            borderRadius: designTokens.borderRadius.lg,
            p: 2,
            boxShadow: designTokens.shadows.xl,
            minWidth: 240
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: 'white',
              fontWeight: designTokens.typography.fontWeight.bold,
              mb: 1.5,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              pb: 1,
              letterSpacing: 0.5
            }}
          >
            {label}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {payload.map((entry, index) => {
              const year = entry.name || '';
              const currentValue = entry.value || 0;

              return (
                <Box key={index} sx={{ mb: index < payload.length - 1 ? 1 : 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, boxShadow: `0 0 8px ${entry.color}88` }} />
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: designTokens.typography.fontWeight.semibold }}>
                        {year}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: designTokens.typography.fontWeight.bold, ml: 3 }}>
                      €{currentValue.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.8, pl: 2.2 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      <FormattedMessage id="annualTotal" defaultMessage="Annual Total" />
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: designTokens.typography.fontWeight.medium }}>
                      €{(annualTotals[year] || 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card
      sx={{
        borderRadius: designTokens.borderRadius.lg,
        boxShadow: designTokens.shadows.md,
        border: designTokens.glassmorphism.border,
        background: designTokens.colors.background.paper,
        transition: `all ${designTokens.transitions.normal}`,
        '&:hover': {
          boxShadow: designTokens.shadows.lg,
        },
      }}
    >
      <CardContent sx={{ p: designTokens.spacing.lg }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: designTokens.typography.fontWeight.semibold,
            color: designTokens.colors.text.primary,
            mb: 3
          }}
        >
          <FormattedMessage id="categoryExpenseEvolution" />
        </Typography>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleSelectAll}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: designTokens.borderRadius.md,
                textTransform: 'none',
                borderColor: designTokens.colors.primary.main,
                color: designTokens.colors.primary.main,
              }}
            >
              <FormattedMessage id="selectAll" />
            </Button>
            <Button
              onClick={handleDeselectAll}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: designTokens.borderRadius.md,
                textTransform: 'none',
                borderColor: designTokens.colors.text.disabled,
                color: designTokens.colors.text.secondary,
              }}
            >
              <FormattedMessage id="deselectAll" />
            </Button>
          </Box>
        </Box>
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {categories.map((category, index) => (
            <Chip
              key={category}
              label={category}
              onClick={() => handleCategoryToggle(category)}
              sx={{
                borderRadius: designTokens.borderRadius.md,
                fontWeight: designTokens.typography.fontWeight.medium,
                background: selectedCategories.includes(category)
                  ? designTokens.colors.primary.gradient
                  : designTokens.colors.background.elevated,
                color: selectedCategories.includes(category) ? 'white' : designTokens.colors.text.primary,
                transition: `all ${designTokens.transitions.fast}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: designTokens.shadows.sm,
                  background: selectedCategories.includes(category)
                    ? designTokens.colors.primary.gradient
                    : designTokens.colors.background.elevated,
                  opacity: 0.9,
                },
              }}
            />
          ))}
        </Box>
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={designTokens.colors.background.elevated} vertical={false} />
              <XAxis
                dataKey="name"
                stroke={designTokens.colors.text.secondary}
                fontSize={12}
                tickLine={false}
                axisLine={false}
                dy={10}
              />
              <YAxis
                stroke={designTokens.colors.text.secondary}
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `€${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                align="right"
                height={36}
                iconType="circle"
                wrapperStyle={{ paddingTop: 10 }}
              />
              {yearsAvailable.map((year, index) => (
                <Line
                  key={year}
                  type="monotone"
                  dataKey={year}
                  name={year}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={3}
                  dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                  activeDot={{ r: 8, strokeWidth: 0 }}
                  animationDuration={1500}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
        {selectedCategories.length === 0 && (
          <Typography align="center" sx={{ mt: 2, color: designTokens.colors.text.disabled }}>
            <FormattedMessage id="selectCategoriesForChart" />
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CategoryExpenseChart;